<template>
    <section class="uploader-shell" :aria-busy="loading" aria-labelledby="uploader-title">
        <header class="uploader-header">
            <div class="uploader-brandline">
                <span class="uploader-eyebrow"><span class="uploader-brand-mark" aria-hidden="true">▦</span> {{ $t('design_eyebrow') }}</span>
                <span class="uploader-free-badge">{{ $t('design_free') }}</span>
            </div>
            <ol v-if="!loading && !error" class="uploader-steps" :aria-label="$t('design_steps')">
                <li v-for="number in stepCount" :key="number" :class="{'is-current': $store.getters.step === number, 'is-complete': $store.getters.step > number}" :aria-current="$store.getters.step === number ? 'step' : undefined">
                    <span class="uploader-step-dot" aria-hidden="true">{{ $store.getters.step > number ? '✓' : number }}</span>
                    <span class="uploader-step-label">{{ $t('design_step_' + number) }}</span>
                </li>
            </ol>
            <div class="uploader-heading" aria-live="polite">
                <p v-if="!loading && !error" class="uploader-mobile-step">{{ $t('design_step_count', {step: $store.getters.step, total: stepCount}) }}</p>
                <h2 id="uploader-title">{{ $t(loading ? 'design_loading' : 'design_title_' + $store.getters.step) }}</h2>
                <p v-if="!loading && !error">{{ $t('design_description_' + $store.getters.step) }}</p>
            </div>
        </header>
        <div v-if="loading" class="uploader-loading" role="status"><span class="uploader-spinner" aria-hidden="true"></span>{{ $t('design_loading') }}</div>
        <div v-if="!loading && error" class="alert-uploader" role="alert">{{ error }}</div>
        <div class="step-container">
            <step-one v-if="!loading && !error && this.$store.getters.step === 1" v-on:scroll-to-app="scrollToApp" :country="this.country" :preview="this.$store.getters.preview"></step-one>
            <step-two v-if="!loading && !error && this.$store.getters.step === 2" v-on:scroll-to-app="scrollToApp"></step-two>
            <step-three v-if="!loading && !error && this.$store.getters.step === 3" v-on:scroll-to-app="scrollToApp" v-bind:product_groups="product_groups"></step-three>
            <step-four v-if="!loading && !error && this.$store.getters.step === 4" v-on:scroll-to-app="scrollToApp"></step-four>
            <step-five v-if="!loading && !error && this.$store.getters.step === 5" v-on:scroll-to-app="scrollToApp" v-on:finish-preview="finishPreview()"></step-five>
        </div>
        <footer v-if="!loading && !error" class="uploader-footer">
            <span class="uploader-privacy"><svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M12 3 4 6v6c0 4 8 9 8 9s8-5 8-9V6z"/><path d="m8 12 3 3 5-6"/></svg>{{ $t('design_privacy') }}</span>
            <span v-if="$store.getters.preview" class="uploader-preview-code">{{ $t('preview_code', {preview_code: $store.getters.preview}) }}</span>
        </footer>
    </section>
</template>

<script>
    import StepOne from "@/components/StepOne";
    import StepTwo from "@/components/StepTwo";
    import StepThree from "@/components/StepThree";
    import StepFour from "@/components/StepFour";
    import StepFive from "@/components/StepFive";

    import ProductService from "@/services/product.service";
    import * as types from "@/store/mutation-types";
    import PreviewService from "@/services/preview.service";
    import UploadLog from "@/services/upload-log.service";

    const LAST_STEP = 5;

    export default {
        name: "Upload",
        data: function() {
            return {
                "preview": "",
                "loading": true,
                "error": null,
                "stepCount": LAST_STEP,
                "product_groups": [],
                "stickyTop": 0
            }
        },
        props: [
            'country'
        ],
        components: {
            StepOne,
            StepTwo,
            StepThree,
            StepFour,
            StepFive
        },
        methods: {
            finishPreview: function() {
                //let vm = this;

                if(Object.prototype.hasOwnProperty.call(window, 'dataLayer') && Array.isArray(window.dataLayer)) {
                    window.dataLayer.push({
                        'event': 'preview-start-new'
                    });
                }
                UploadLog.log('preview_start_new');
                this.$store.dispatch(types.RESET)
                this.$store.dispatch(types.RESET_HEADERCOUNT);
                this.$store.dispatch(types.RESET_TILECOUNT);
                this.$store.dispatch(types.TILE_IMAGES_RESET);
                this.$store.dispatch(types.FINISH_PREVIEW);

                window.location.reload();
            },
            startLogging: function(resumed) {
                UploadLog.setPreview(this.$store.getters.preview);
                UploadLog.installGlobalHandlers();
                UploadLog.logSessionStart({
                    resumed: resumed,
                    step: this.$store.getters.step,
                    header_count: this.$store.getters.header_count,
                    tile_count: this.$store.getters.tile_count,
                    has_email: Boolean(this.$store.getters.email),
                    country: this.country
                });
            },
            // Height of whatever the host page keeps fixed at the top of the viewport
            // (e.g. the sticky WordPress navigation), so the card is not scrolled underneath it.
            stickyTopOffset: function() {
                const app = document.getElementById('app');
                let offset = 0;
                for (const element of document.elementsFromPoint(window.innerWidth / 2, 1)) {
                    if(app.contains(element) || element === document.documentElement || element === document.body) {
                        continue;
                    }
                    const position = window.getComputedStyle(element).position;
                    const rect = element.getBoundingClientRect();
                    if((position === 'fixed' || position === 'sticky') && rect.height < window.innerHeight / 2) {
                        offset = Math.max(offset, rect.bottom);
                    }
                }
                return offset;
            },
            // Publishes the sticky-navigation height as --uploader-sticky-top so the CSS can size
            // the upload areas to the viewport. The host's navigation only becomes sticky after
            // scrolling, so the value is latched to the largest offset seen.
            updateStickyTopOffset: function() {
                const offset = this.stickyTopOffset();
                if(offset > this.stickyTop) {
                    this.stickyTop = offset;
                    this.$el.style.setProperty('--uploader-sticky-top', offset + 'px');
                }
            },
            watchStickyTopOffset: function() {
                this.updateStickyTopOffset();
                window.addEventListener('scroll', this.updateStickyTopOffset, {passive: true});
                window.addEventListener('resize', this.updateStickyTopOffset, {passive: true});
            },
            scrollToApp: function() {
                this.updateStickyTopOffset();
                const element = document.getElementById('app');
                const y = element.getBoundingClientRect().top + window.pageYOffset - this.stickyTop - 16;

                window.scrollTo({top: Math.max(0, y), behavior: 'smooth'});
            }
        },
        watch: {
            '$store.getters.step': function(step, previousStep) {
                UploadLog.log('step_reached', {
                    step: step,
                    from: previousStep,
                    header_count: this.$store.getters.header_count,
                    tile_count: this.$store.getters.tile_count
                });
            }
        },
        beforeUnmount() {
            window.removeEventListener('scroll', this.updateStickyTopOffset);
            window.removeEventListener('resize', this.updateStickyTopOffset);
        },
        mounted() {
            let vm = this;
            this.watchStickyTopOffset();
            ProductService.list(this.country).then(
                response => {
                    this.product_groups = response.data;

                    let dateDiff = Math.abs(Date.now() - this.$store.getters.preview_date);
                    if(this.$store.getters.step === 0 ||
                        this.$store.getters.step > LAST_STEP ||
                        this.$store.getters.preview == null ||
                        this.$store.getters.preview === "" ||
                        dateDiff > (24 * 60 * 60 * 1000)) {

                        this.$store.dispatch(types.RESET)
                        this.$store.dispatch(types.RESET_HEADERCOUNT);
                        this.$store.dispatch(types.RESET_TILECOUNT);
                        this.$store.dispatch(types.TILE_IMAGES_RESET);
                        this.$store.dispatch(types.FINISH_PREVIEW);

                        PreviewService.create(vm.country).then(
                            response => {
                                this.$store.dispatch(types.NEW_PREVIEW, {
                                    preview: response.data
                                });
                                vm.startLogging(false);
                            },
                            error => {
                                console.log((error.response && error.response.data) ||
                                    error.message ||
                                    error.toString());
                                this.error = this.$t('preview_creation_failed');
                            }
                        ).then(function () {
                          vm.loading = false;
                        });
                    } else {
                        vm.startLogging(true);
                        vm.loading = false;
                    }
                },
                error => {
                    console.log((error.response && error.response.data) ||
                        error.message ||
                        error.toString());
                    vm.error = vm.$t('preview_creation_failed');
                    vm.loading = false;
                }
            )


        }
    }
</script>

<style scoped>

</style>
